import { supabase } from '../clients/supabaseClient';

// ── Types ──────────────────────────────────────────────────────────────────────

// Parameters accepted by the public fetchAnalyticsFromDb function.
// `from` and `to` are ISO date strings defining the current period window.
// `topLimit` caps how many top products/categories are returned (default 10).
// `storeId` optionally scopes all queries to a single store.
export type AnalyticsParams = {
    from: string;
    to: string;
    topLimit?: number;
    storeId?: string;
};

// Minimal order shape fetched from the database — only the columns we actually need.
type OrderRow = {
    id: string;
    status: string;
    created_at: string;
    store_id: string | null;
    delivery_fee: number | null;
};

// The full shape returned to the UI after all aggregation is complete.
// All monetary values are in rands (divided by 100 from stored cents).
export type AnalyticsResponse = {
    kpis: {
        total_orders: number;
        revenue: number;
        cancelled_revenue?: number;
        wallet_refunds?: number;
        delivery_fee_revenue: number;
        product_revenue: number;
        avg_order_value: number;
        conversion_rate: number | null;
        // Period-over-period percentage change for each KPI (null = no previous data).
        deltas?: {
            total_orders?: number | null;
            revenue?: number | null;
            avg_order_value?: number | null;
            conversion_rate?: number | null;
            completed_orders?: number | null;
            cancelled_orders?: number | null;
            amount_refunded?: number | null;
        };
    };
    orders_by_status: { status: string; count: number }[];
    sales_over_time:  { date: string; orders: number; revenue: number }[];
    top_products: {
        product_id: string | number;
        sku: string;
        name: string;
        units_sold: number;
        revenue: number;
        // Breakdown of how many units sold at each price point (useful when prices change over time).
        price_buckets: { price: number; units_sold: number }[];
    }[];
    top_categories: {
        category_id: string;
        name: string;
        units_sold: number;
        revenue: number;
    }[];
};

// ── Helpers ────────────────────────────────────────────────────────────────────

// Converts a full ISO timestamp into a YYYY-MM-DD string in UTC.
// Used as the map key when bucketing orders by day for the sales_over_time series.
const dateKey = (iso: string) => {
    const d = new Date(iso);
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
};

// Fetches a lightweight list of orders within a date range.
// Optionally filters to a single store. Only selects the columns needed for
// aggregation so we avoid pulling unnecessary data from the database.
async function fetchOrdersInRange(
    from: string,
    to: string,
    storeId?: string
): Promise<OrderRow[]> {
    let query = supabase
        .from('orders')
        .select('id, status, created_at, store_id, delivery_fee')
        .gte('created_at', from)
        .lte('created_at', to);

    if (storeId) query = query.eq('store_id', storeId);

    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []) as OrderRow[];
}

// Core aggregation function. Given a list of order rows, it:
//   1. Counts orders per status and per day.
//   2. Fetches line items for DELIVERY_COMPLETE orders only (revenue-generating).
//   3. Fetches wallet credit transactions across all orders (refunds).
//   4. Optionally resolves product → category links for the top N products.
//
// `includeProductBreakdowns` should be true for the current period and false
// for the previous period (we only need category/product data for the current view).
async function buildRevenueFromOrders(
    orderRows: OrderRow[],
    options: { includeProductBreakdowns?: boolean; topLimit?: number } = {}
) {
    const { includeProductBreakdowns = false, topLimit = 10 } = options;

    // Split into completed vs all — revenue is only counted for completed deliveries.
    const completedRows = orderRows.filter(o => o.status === 'DELIVERY_COMPLETE');
    const completedIds  = completedRows.map(o => o.id);
    const allIds        = orderRows.map(o => o.id);

    // dayMap: YYYY-MM-DD → { orders completed that day, revenue in cents that day }
    // statusMap: status string → count of orders with that status
    const dayMap    = new Map<string, { orders: number; revenue: number }>();
    const statusMap = new Map<string, number>();

    // First pass: populate status counts and initialise day buckets.
    // Revenue is added later once we have line-item data.
    for (const row of orderRows) {
        const status = row.status ?? 'UNKNOWN';
        statusMap.set(status, (statusMap.get(status) ?? 0) + 1);
        const key   = dateKey(row.created_at);
        const entry = dayMap.get(key) ?? { orders: 0, revenue: 0 };
        if (row.status === 'DELIVERY_COMPLETE') entry.orders += 1;
        dayMap.set(key, entry);
    }

    // Running cent totals — divided by 100 before being returned.
    let totalProductCents  = 0;
    let totalDeliveryCents = 0;
    let totalRefundsCents  = 0;

    // productAgg: product_id → aggregated units, revenue, and per-price-point breakdown.
    const productAgg = new Map<string, {
        product_id: string;
        sku: string;
        name: string;
        units_sold: number;
        revenue: number;
        // price_buckets maps price-in-cents → units sold at that price.
        price_buckets: Map<number, number>;
    }>();

    // categoryAgg: category_id → aggregated units and revenue across its products.
    const categoryAgg = new Map<string, {
        category_id: string;
        name: string;
        units_sold: number;
        revenue: number;
    }>();

    if (allIds.length > 0) {
        // Fetch line items and wallet refunds in parallel to minimise round trips.
        //
        // order_items: only for completed orders (revenue-generating).
        //   - Joins products for name/SKU and product_prices for the price at time of sale.
        //   - Excludes soft-deleted items (deleted_at IS NULL).
        //
        // wallet_transactions: credit direction = money going back to the customer (refunds).
        //   - Fetched across ALL orders (refunds can occur on non-completed orders too).
        const [itemsRes, walletRes] = await Promise.all([
            supabase
                .from('order_items')
                .select('order_id, product_id, quantity, product:products(id, sku, name), price_record:product_prices(price)')
                .in('order_id', completedIds)
                .is('deleted_at', null),
            supabase
                .from('wallet_transactions')
                .select('amount_cents, source_order_id, direction')
                .in('source_order_id', allIds)
                .eq('direction', 'credit'),
        ]);

        if (itemsRes.error)  throw itemsRes.error;
        if (walletRes.error) throw walletRes.error;

        // Sum all wallet credit amounts into the refunds total.
        for (const tx of walletRes.data ?? []) {
            totalRefundsCents += Number(tx.amount_cents ?? 0);
        }

        // Process each line item: accumulate product-level revenue and
        // add the line total to the matching day bucket for the sales chart.
        for (const row of itemsRes.data ?? []) {
            // Supabase returns joined rows as arrays when using the object-relation syntax.
            const product    = Array.isArray(row.product)    ? row.product[0]    : row.product;
            const priceRecord = Array.isArray(row.price_record) ? row.price_record[0] : row.price_record;
            const priceCents = Number(priceRecord?.price ?? 0);
            const qty        = Number(row.quantity ?? 0);
            const lineCents  = qty * priceCents;

            totalProductCents += lineCents;

            // Add this line's revenue to the correct day in the sales-over-time map.
            const matchingOrder = completedRows.find(o => o.id === row.order_id);
            if (matchingOrder) {
                const key   = dateKey(matchingOrder.created_at);
                const entry = dayMap.get(key) ?? { orders: 0, revenue: 0 };
                entry.revenue += lineCents;
                dayMap.set(key, entry);
            }

            // Upsert this product into the aggregation map.
            const id = String(product?.id ?? row.product_id ?? 'unknown');
            const existing = productAgg.get(id) ?? {
                product_id:    id,
                sku:           String(product?.sku  ?? ''),
                name:          String(product?.name ?? 'Unnamed'),
                units_sold:    0,
                revenue:       0,
                price_buckets: new Map<number, number>(),
            };
            existing.units_sold += qty;
            existing.revenue    += lineCents;
            // Track units sold at this specific price point (price may vary across orders).
            existing.price_buckets.set(priceCents, (existing.price_buckets.get(priceCents) ?? 0) + qty);
            productAgg.set(id, existing);
        }

        // Only fetch category data when building the current-period result —
        // the previous period only needs totals for delta calculations.
        if (includeProductBreakdowns) {
            // Limit category lookup to the top N products to avoid an overly large IN clause.
            const topProductIds = Array.from(productAgg.keys()).slice(0, topLimit);
            if (topProductIds.length > 0) {
                const { data: catLinks, error: catErr } = await supabase
                    .from('category_products')
                    .select('product_id, category_id, categories(id, name)')
                    .in('product_id', topProductIds);

                if (catErr) throw catErr;

                // Roll up each product's revenue and units into its parent category.
                for (const link of catLinks ?? []) {
                    const cat = Array.isArray(link.categories) ? link.categories[0] : link.categories;
                    if (!cat) continue;
                    const prod = productAgg.get(link.product_id);
                    if (!prod) continue;
                    const catId    = String(cat.id);
                    const existing = categoryAgg.get(catId) ?? {
                        category_id: catId, name: String(cat.name), units_sold: 0, revenue: 0,
                    };
                    existing.units_sold += prod.units_sold;
                    existing.revenue    += prod.revenue;
                    categoryAgg.set(catId, existing);
                }
            }
        }
    }

    // Add delivery fees to the day buckets and running total.
    // Delivery fee lives on the order row itself, not on line items.
    for (const row of orderRows) {
        if (row.status === 'DELIVERY_COMPLETE') {
            const feeCents = Number(row.delivery_fee ?? 0);
            totalDeliveryCents += feeCents;
            const entry = dayMap.get(dateKey(row.created_at));
            if (entry) entry.revenue += feeCents;
        }
    }

    const completedCount = completedRows.length;
    const totalRevenue   = (totalProductCents + totalDeliveryCents) / 100;

    // Returns the percentage change between two values.
    // Returns null when the previous value is 0 and current is non-zero
    // (can't divide by zero, and showing ∞% would be misleading).
    const calcDelta = (cur: number, prev: number): number | null => {
        if (prev === 0) return cur === 0 ? 0 : null;
        return ((cur - prev) / prev) * 100;
    };

    return {
        total_orders:         orderRows.length,
        completed_orders:     completedCount,
        revenue:              totalRevenue,
        wallet_refunds:       totalRefundsCents  / 100,
        product_revenue:      totalProductCents  / 100,
        delivery_fee_revenue: totalDeliveryCents / 100,
        avg_order_value:      completedCount > 0 ? totalRevenue / completedCount : 0,
        calcDelta,
        // Status breakdown sorted descending by count.
        orders_by_status: Array.from(statusMap.entries())
            .map(([status, count]) => ({ status, count }))
            .sort((a, b) => b.count - a.count),
        // Daily sales series sorted ascending by date.
        sales_over_time: Array.from(dayMap.entries())
            .map(([date, v]) => ({ date, orders: v.orders, revenue: v.revenue / 100 }))
            .sort((a, b) => (a.date < b.date ? -1 : 1)),
        // Top products by units sold, with per-price-point breakdowns converted to rands.
        top_products: includeProductBreakdowns
            ? Array.from(productAgg.values())
                .map(p => ({
                    product_id:   p.product_id,
                    sku:          p.sku,
                    name:         p.name,
                    units_sold:   p.units_sold,
                    revenue:      p.revenue / 100,
                    price_buckets: Array.from(p.price_buckets.entries())
                        .map(([priceCents, units]) => ({ price: priceCents / 100, units_sold: units }))
                        .sort((a, b) => a.price - b.price),
                }))
                .sort((a, b) => b.units_sold - a.units_sold)
                .slice(0, topLimit)
            : [],
        // Top categories by revenue, capped at topLimit.
        top_categories: includeProductBreakdowns
            ? Array.from(categoryAgg.values())
                .map(c => ({ category_id: c.category_id, name: c.name, units_sold: c.units_sold, revenue: c.revenue / 100 }))
                .sort((a, b) => b.revenue - a.revenue)
                .slice(0, topLimit)
            : [],
    };
}

// ── Public API ─────────────────────────────────────────────────────────────────

// Entry point called by useAnalytics.
//
// Strategy:
//   1. Derive a "previous period" of equal length immediately before the current one.
//      e.g. if `from`/`to` spans 7 days, the previous period is the 7 days before that.
//   2. Fetch both periods' orders in parallel.
//   3. Run full aggregation (with product/category breakdowns) on the current period,
//      and a lightweight aggregation (totals only) on the previous period.
//   4. Use the previous-period totals solely to compute period-over-period deltas.
export async function fetchAnalyticsFromDb(params: AnalyticsParams): Promise<AnalyticsResponse> {
    const { from, to, topLimit = 10, storeId } = params;

    // Calculate the previous period window of identical length.
    const currentFrom  = new Date(from);
    const currentTo    = new Date(to);
    const periodMs     = currentTo.getTime() - currentFrom.getTime();
    const previousFrom = new Date(currentFrom.getTime() - periodMs - 1);
    const previousTo   = new Date(currentTo.getTime()   - periodMs - 1);

    // Fetch both periods' raw order rows in parallel.
    const [currentOrders, previousOrders] = await Promise.all([
        fetchOrdersInRange(from, to, storeId),
        fetchOrdersInRange(previousFrom.toISOString(), previousTo.toISOString(), storeId),
    ]);

    // Aggregate both periods. Only the current period needs product/category detail.
    const [current, previous] = await Promise.all([
        buildRevenueFromOrders(currentOrders,  { includeProductBreakdowns: true,  topLimit }),
        buildRevenueFromOrders(previousOrders, { includeProductBreakdowns: false, topLimit }),
    ]);

    return {
        kpis: {
            total_orders:         current.total_orders,
            revenue:              current.revenue,
            product_revenue:      current.product_revenue,
            wallet_refunds:       current.wallet_refunds,
            delivery_fee_revenue: current.delivery_fee_revenue,
            avg_order_value:      current.avg_order_value,
            conversion_rate:      null, // Not yet implemented
            deltas: {
                total_orders:     current.calcDelta(current.total_orders,     previous.total_orders),
                revenue:          current.calcDelta(current.revenue,          previous.revenue),
                avg_order_value:  current.calcDelta(current.avg_order_value,  previous.avg_order_value),
                completed_orders: current.calcDelta(current.completed_orders, previous.completed_orders),
                conversion_rate:  null, // Not yet implemented
            },
        },
        orders_by_status: current.orders_by_status,
        sales_over_time:  current.sales_over_time,
        top_products:     current.top_products,
        top_categories:   current.top_categories,
    };
}